/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';

import DateTimeUtils from 'js/utils/DateTimeUtils';

import { typography2 } from '@coursera/cds-core';
import { type EventData, useTracker } from '@coursera/event-pulse/react';

import type { Notification as NotificationType } from 'bundles/notification-center/types';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

type TrackNotificationProps = EventData<'view_notification' | 'click_notification'>;

type Props = {
  href: string;
  children: React.ReactNode;
  notification: NotificationType;
  rank?: number;
  trackingData?: Omit<TrackNotificationProps, 'notification'>;
  onClick: (notification: NotificationType) => void;
};

const styles = {
  notification: css`
    ${typography2.bodyPrimary};
    display: block;
    padding: var(--cds-spacing-150) var(--cds-spacing-100);
    text-decoration: none;
    word-break: break-word;
    color: var(--cds-color-interactive-secondary);

    &:hover,
    &:focus,
    &:active {
      color: var(--cds-color-interactive-secondary-hover);
      text-decoration: underline;
    }
  `,
  notificationRead: css`
    color: var(--cds-color-neutral-primary-weak);

    &:hover,
    &:focus,
    &:active {
      color: var(--cds-color-neutral-primary-weak);
      text-decoration: underline;
    }
  `,
  notificationTimestamp: css`
    ${typography2.bodySecondary};
    color: var(--cds-color-neutral-primary-weak);
    font-size: 12px;
    margin-top: 5px;
  `,
};
// TODO: Limit length of notification text
const Notification: React.FC<Props> = ({ href, notification, rank, onClick, trackingData, children }) => {
  const { messageType, id, isRead } = notification;
  const eventingData: TrackNotificationProps = {
    notification: {
      templateId: messageType,
      channelType: 'notification_center' as const,
      id,
      rank,
      isPreviouslyRead: isRead,
      redirectUrl: href,
    },
    ...(trackingData || {}),
  };
  const track = useTracker();

  /* Cannot use visibility tracker as TrackedLink2 does not accept ref */
  useEffect(() => {
    track('view_notification', eventingData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification]);

  const handleClick = () => {
    track('click_notification', eventingData);
    onClick(notification);
  };

  return (
    <TrackedLink2
      href={href}
      trackingName="notification"
      data={{
        id: notification.id,
        isRead: notification.isRead,
        createdAt: notification.createdAt,
        messageType: notification.messageType,
      }}
      onClick={handleClick}
      data-testid="notification"
      css={[styles.notification, notification.isRead && styles.notificationRead]}
    >
      {children}

      <div css={styles.notificationTimestamp}>
        {DateTimeUtils.momentWithUserTimezone(notification.createdAt).fromNow()}
      </div>
    </TrackedLink2>
  );
};

export default Notification;
